import { Divider, Heading, OrderedList } from '@chakra-ui/react'
import { Body, Button, QRCode } from '@missionlabs/react'
import { Destination } from '@missionlabs/types'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'

import { DeviceProvisioningFlow } from '../../DeviceProvisioningFlow'

export type ActivateESimInstructionsProps = Pick<Destination, 'metadata' | 'provisioningFlow'>

const getTranslatedInstructions = t => [
    {
        title: t('devices.add.eSim.activation.instructions.android_users.title'),
        steps: [
            {
                title: t(
                    'devices.add.eSim.activation.instructions.android_users.steps.open_settings'
                )
            },
            {
                title: t(
                    'devices.add.eSim.activation.instructions.android_users.steps.check_connections'
                )
            },
            {
                title: t(
                    'devices.add.eSim.activation.instructions.android_users.steps.tap_mobile_network'
                )
            },
            {
                title: t(
                    'devices.add.eSim.activation.instructions.android_users.steps.find_and_select'
                )
            },
            {
                title: t(
                    'devices.add.eSim.activation.instructions.android_users.steps.choose_between_scanning'
                )
            },
            {
                title: t(
                    'devices.add.eSim.activation.instructions.android_users.steps.follow_instructions'
                )
            }
        ]
    },
    {
        title: t('devices.add.eSim.activation.instructions.ios_users.title'),
        steps: [
            {
                title: t(
                    'devices.add.eSim.activation.instructions.ios_users.steps.if_ios_later.title'
                ),
                substeps: [
                    {
                        title: t(
                            'devices.add.eSim.activation.instructions.ios_users.steps.if_ios_later.substeps.tap_continue'
                        )
                    },
                    {
                        title: t(
                            'devices.add.eSim.activation.instructions.ios_users.steps.if_ios_later.substeps.tap_add_plan'
                        )
                    }
                ]
            },
            {
                title: t(
                    'devices.add.eSim.activation.instructions.ios_users.steps.if_ios_earlier.title'
                ),
                substeps: [
                    {
                        title: t(
                            'devices.add.eSim.activation.instructions.ios_users.steps.if_ios_earlier.substeps.when_plan_detected'
                        )
                    },
                    {
                        title: t(
                            'devices.add.eSim.activation.instructions.ios_users.steps.if_ios_earlier.substeps.follow_instructions'
                        )
                    }
                ]
            }
        ]
    },
    {
        title: t('devices.add.eSim.activation.instructions.ios_manual.title'),
        steps: [
            {
                title: t('devices.add.eSim.activation.instructions.ios_manual.steps.open_settings')
            },
            {
                title: t('devices.add.eSim.activation.instructions.ios_manual.steps.select_mobile')
            },
            {
                title: t('devices.add.eSim.activation.instructions.ios_manual.steps.select_qr')
            },
            {
                title: t(
                    'devices.add.eSim.activation.instructions.ios_manual.steps.select_enter_details'
                )
            },
            {
                title: t(
                    'devices.add.eSim.activation.instructions.ios_manual.steps.follow_instructions'
                )
            }
        ]
    }
]

export const ActivateESimInstructionsStep: FC<ActivateESimInstructionsProps> = ({
    metadata = {},
    provisioningFlow
}) => {
    const { t } = useTranslation()

    const instructions = getTranslatedInstructions(t)

    const provisioningFlowSteps = provisioningFlow ?? []
    const currentProvisioningFlowStep =
        provisioningFlowSteps.findLastIndex(item => item.success) + 1

    return (
        <Body>
            <Heading size="h2">{t('devices.add.eSim.activation.title')}</Heading>
            <Body mb="12px">{t('devices.add.eSim.activation.subtitle')}</Body>
            <QRCode url={metadata.lpa} />
            <Body mt="24px">{t('devices.add.eSim.activation.help', { ...metadata })}</Body>
            <Divider my="24px" />
            <DeviceProvisioningFlow
                steps={provisioningFlowSteps}
                stepIndex={currentProvisioningFlowStep}
            />
            {instructions.map((instruction, index) => (
                <>
                    <Divider my="24px" />
                    <Body key={`instruction-${index}-${instruction.title}`}>
                        {instruction.title}
                    </Body>
                    <OrderedList ml="24px">
                        {instruction.steps.map((step, index) => (
                            <Body key={`step-${index}-${step.title}`} as="li" size="sm">
                                {step.title}
                                {step.substeps && (
                                    <OrderedList styleType="lower-alpha" ml="24px">
                                        {step.substeps.map((substep, index) => (
                                            <Body
                                                key={`substep-${index}-${substep.title}`}
                                                as="li"
                                                size="sm"
                                            >
                                                {substep.title}
                                            </Body>
                                        ))}
                                    </OrderedList>
                                )}
                            </Body>
                        ))}
                    </OrderedList>
                </>
            ))}
            <Divider my="24px" />
            <Body>{t('devices.add.eSim.activation.send_email_text')}</Body>
            <Button type="submit" mt="12px" size="md" variant="creationary">
                {t('devices.add.eSim.activation.send_email')}
            </Button>
        </Body>
    )
}
